/**
 * WIRING SIGN-IN IN, AND THE ONE THING THAT MUST HAPPEN WHEN IT CANNOT BE.
 * ======================================================================
 *
 * `installOidcAuth()` is called once at startup. There are exactly two outcomes and the second one is
 * the interesting one:
 *
 *  1. **Configured** → an {@link OidcAuth} is built, installed through `setTokenProvider()`, the
 *     redirect is handled if the URL is a callback, and the proactive-refresh triggers are started.
 *  2. **Not configured** → **`setTokenProvider()` IS NOT CALLED.** The `devStubTokenProvider` stays
 *     active, so `api.ts` keeps answering `auth-not-configured` and **keeps sending nothing at all**.
 *
 * Outcome 2 is not a fallback and it is not degradation; it is the correct behaviour and it is why this
 * function exists rather than a constructor call in `main.tsx`. `src/core/auth.ts` and `src/core/api.ts`
 * are built around the rule that *"a stub must never produce something that looks like a session, and
 * an unauthenticated request would come back 401 and read like an expired one"*. Installing a real
 * provider with no client id would break precisely that: every request would be attempted, every one
 * would fail, and the screen would say "not signed in" — sending a technician to look for a password
 * problem that does not exist while the actual cause is a missing build variable.
 *
 * The failure is loud in three places at once, because a build shipped without a client id must not be
 * something anyone has to infer from behaviour:
 *  · a `console.error` naming every missing variable (the same idiom `core/auth.ts` uses for the stub);
 *  · the returned result, which carries the list for a caller to act on;
 *  · a `not-configured` {@link AuthPhase}, which `SignInBanner` renders in words on the screen.
 *
 * ⚠️ **NOT CALLED FROM `main.tsx` IN THIS CHANGE.** There is no app client id yet — a sibling workstream
 * is obtaining it — and the issuer itself is unresolved (`config.ts` header). Wiring the call now would
 * put every technician on the `not-configured` path, which is exactly where the stub already puts them,
 * with more moving parts and a second banner saying the same thing. The one-line call and the env block
 * (`exampleEnvBlock()`) are what the next change needs; the arrival of a client id should not require
 * anyone to understand this module.
 */

import { setTokenProvider, tokenProvider } from '../core/auth';
import { readOidcConfig, type MissingConfigValue } from './config';
import { OidcAuth, type RedirectResult } from './oidc';
import type { AuthPhase } from './phase';

export type InstallResult =
  | {
      readonly ok: true;
      readonly auth: OidcAuth;
      /** What the startup URL turned out to be. `no-callback` on an ordinary load. */
      readonly redirect: RedirectResult;
      readonly stop: () => void;
    }
  | {
      readonly ok: false;
      readonly reason: 'not-configured' | 'provider-already-set';
      readonly missing: readonly MissingConfigValue[];
      readonly message: string;
      /** The phase a UI should render. Never `never-signed-in` — nothing was ever attempted. */
      readonly phase: AuthPhase;
    };

export interface InstallOptions {
  /** Overridden in tests. Defaults to `import.meta.env`. */
  readonly env?: Readonly<Record<string, unknown>>;
  readonly origin?: string;
  readonly fetchImpl?: typeof fetch;
  /** Skip attaching `online`/`visibilitychange`/poll triggers. Tests do not want live listeners. */
  readonly startTriggers?: boolean;
  /** Skip the startup redirect handling. Tests drive `handleRedirect` themselves. */
  readonly handleRedirect?: boolean;
}

export async function installOidcAuth(options: InstallOptions = {}): Promise<InstallResult> {
  const result = readOidcConfig(
    options.env ?? ((import.meta.env ?? {}) as Readonly<Record<string, unknown>>),
    options.origin ?? globalThis.location?.origin,
  );

  if (!result.ok) {
    // eslint-disable-next-line no-console -- a build with no sign-in configuration must be visible in
    // a console rather than inferred from the app quietly sending nothing.
    console.error(`[lockercustody] ${result.message}`);
    return {
      ok: false,
      reason: 'not-configured',
      missing: result.missing,
      message: result.message,
      phase: { kind: 'not-configured', detail: result.missing.map((entry) => entry.variable).join(', ') },
    };
  }

  if (tokenProvider().kind !== 'stub') {
    /*
     * `setTokenProvider` throws on a second call, by design — it exists so a real provider cannot be
     * silently replaced by a stub later in the session. Reaching that throw here would mean a double
     * install, which is a startup bug, so it is reported rather than allowed to crash the app on load.
     */
    return {
      ok: false,
      reason: 'provider-already-set',
      missing: [],
      message: 'a token provider is already installed; installOidcAuth() must be called exactly once',
      phase: { kind: 'not-configured', detail: 'double install' },
    };
  }

  const auth = new OidcAuth({
    config: result.config,
    ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
  });
  setTokenProvider(auth);

  /*
   * The redirect is handled BEFORE the triggers start. A callback URL carries a code that has to be
   * exchanged before anything asks for a token; starting the refresher first would have it look at an
   * empty token set and conclude there is nothing to do, which is harmless but reads like a race to
   * whoever debugs it next.
   */
  const redirect =
    options.handleRedirect === false ? ({ kind: 'no-callback' } as RedirectResult) : await auth.handleRedirect();

  const stop = options.startTriggers === false ? (): void => undefined : auth.start();

  return { ok: true, auth, redirect, stop };
}
